// src/components/HeroSection.tsx
import { useWallet } from "../context/WalletContext";
import { HERO } from "../data/content.js";

const CHIP_STYLES: Record<string, { bg: string; border: string; color: string }> = {
  chain: { bg: "rgba(6,182,212,.08)",  border: "rgba(6,182,212,.3)",  color: "#67e8f9" },
  mee:   { bg: "rgba(124,58,237,.08)", border: "rgba(124,58,237,.3)", color: "#a78bfa" },
  nft:   { bg: "rgba(245,158,11,.08)", border: "rgba(245,158,11,.3)", color: "#fbbf24" },
  exp:   { bg: "rgba(16,185,129,.08)", border: "rgba(16,185,129,.3)", color: "#34d399" },
};

export default function HeroSection() {
  const { openWalletModal } = useWallet();

  return (
    <section style={{
      display: "grid", gridTemplateColumns: "1fr 1fr",
      minHeight: 420, padding: "60px 48px 48px",
      position: "relative", zIndex: 1,
    }}>
      {/* Left */}
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16,
          fontSize: 11, fontFamily: "var(--mono)", color: "var(--purple4)",
          letterSpacing: ".12em", textTransform: "uppercase",
        }}>
          <span style={{ width: 24, height: 1, background: "var(--purple4)", display: "inline-block" }} />
          MeeChain Network · Chain 13390
        </div>

        <h1 style={{
          fontFamily: "var(--display)", fontSize: "clamp(40px,5vw,68px)",
          fontWeight: 700, lineHeight: 1.05, color: "#fff", marginBottom: 16,
        }}>
          {HERO.title}<br />
          <span style={{ color: "var(--purple3)" }}>{HERO.subtitle}</span>
        </h1>

        <p style={{ fontSize: 16, color: "var(--muted)", lineHeight: 1.7, maxWidth: 420, marginBottom: 32 }}>
          {HERO.desc}
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
          <button onClick={openWalletModal} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "12px 28px",
            fontSize: 14, fontWeight: 700, fontFamily: "var(--sans)",
            background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff",
            border: "none", borderRadius: 10, cursor: "pointer",
            boxShadow: "0 4px 24px rgba(124,58,237,.4)", transition: "all .18s",
          }}>🚀 LAUNCH APP</button>

          <button onClick={() => window.open("https://scan.meechain.live", "_blank")} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "12px 22px",
            fontSize: 14, fontWeight: 600, fontFamily: "var(--sans)",
            background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)",
            color: "var(--text)", borderRadius: 10, cursor: "pointer",
          }}>🔍 VIEW EXPLORER</button>

          <button onClick={openWalletModal} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "12px 22px",
            fontSize: 14, fontWeight: 600, fontFamily: "var(--sans)",
            background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)",
            color: "var(--text)", borderRadius: 10, cursor: "pointer",
          }}>⬡ CONNECT WALLET</button>
        </div>

        {/* Chips */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {HERO.chips.map(chip => {
            const s = CHIP_STYLES[chip.style] ?? CHIP_STYLES.chain;
            return (
              <span key={chip.label} onClick={() => chip.action === "openExplorer" && window.open("https://scan.meechain.live","_blank")} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: s.bg, border: `1px solid ${s.border}`, color: s.color,
                cursor: chip.action ? "pointer" : "default",
              }}>
                {chip.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Right — Orbital */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        <div style={{ position: "relative", width: 380, height: 380 }}>
          {/* Rings */}
          {[
            { size: 340, color: "rgba(124,58,237,.35)", dur: "18s", dir: "normal" },
            { size: 260, color: "rgba(6,182,212,.25)",  dur: "12s", dir: "reverse" },
            { size: 180, color: "rgba(124,58,237,.2)",  dur: "8s",  dir: "normal" },
          ].map((r, i) => (
            <div key={i} style={{
              position: "absolute", borderRadius: "50%",
              width: r.size, height: r.size,
              border: `1px solid ${r.color}`,
              top: "50%", left: "50%",
              transform: "translate(-50%,-50%)",
              animation: `spin ${r.dur} linear infinite ${r.dir}`,
            }} />
          ))}

          {/* Core */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: 130, height: 130, borderRadius: "50%",
            background: "radial-gradient(circle,rgba(124,58,237,.3) 0%,rgba(7,7,15,.8) 70%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 60px rgba(124,58,237,.4),inset 0 0 40px rgba(124,58,237,.1)",
          }}>
            <div style={{
              fontFamily: "var(--display)", fontSize: 48, fontWeight: 900, color: "var(--purple3)",
              textShadow: "0 0 30px rgba(124,58,237,.8)",
              animation: "float 4s ease-in-out infinite",
            }}>M</div>
          </div>

          {/* Labels left */}
          <div style={{
            position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)",
            display: "flex", flexDirection: "column", gap: 10, textAlign: "right",
          }}>
            {HERO.orbitalLabelsLeft.map(l => (
              <div key={l} style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: "var(--muted)", letterSpacing: ".1em" }}>{l}</div>
            ))}
          </div>

          {/* Labels right */}
          <div style={{
            position: "absolute", right: -20, top: "50%", transform: "translateY(-50%)",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            {HERO.orbitalLabelsRight.map(l => (
              <div key={l} style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: "var(--muted)", letterSpacing: ".1em" }}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
